import React, {ReactNode, SetStateAction, useEffect, useState} from 'react';
import {useTable, usePagination, useRowSelect} from 'react-table';
import {useSticky} from 'react-table-sticky';

import ComponentsLoader from 'components/ComponentsLoader/ComponentsLoader';

import iconStateDisabled from "assets/icons/commons/source-state-disabled.svg"
import iconStateSuccess from "assets/icons/commons/source-state-active.svg"
import iconStateError from "assets/icons/commons/source-state-error.svg"
import pencilIcon from 'assets/icons/commons/pencil.svg';
import styles from './Table.module.css'

import type {
    IRowData,
    ITableSettingsLegacy,
} from 'constants/Table/interfaces';

import CustomTableCell from 'components/Tables/WarehouseTable/CustomTableCell'
import IndeterminateCheckbox from 'components/Tables/WarehouseTable/IndeterminateCheckbox'
import HeaderRow from 'components/Tables/WarehouseTable/HeaderRow'

import {parseWarehouseType} from "./parse-warehouse-type";

import {t} from "i18next";
import TableFooter from "../DefaultTable/TableFooter";
import {WarehouseType} from "../../../constants/Warehouses/WarehouseType";
import Icon from "../../commons/Icons";
import getFrequencyBySla from "../../DataSources/FrequencyHelper";
import {useAppSelector} from "../../../redux/hooks";

interface ITableProps {
    // ??? change it
    columns: any;
    data: Array<IRowData>;
    rowDataLoading: boolean;
    productsRequestError: string;
    tableSettings: ITableSettingsLegacy;
    setTableSettings: (data: SetStateAction<ITableSettingsLegacy>) => void;
    totalProducts: number;
    tableSettingsLoaded: boolean;
    editRow: (row: IRowData) => void;
    setSelectedRows: any;
}

const Table: React.FC<ITableProps> = React.memo(
    ({
         columns,
         data,
         rowDataLoading,
         productsRequestError,
         tableSettings,
         setTableSettings,
         totalProducts,
         tableSettingsLoaded,
         editRow,
         setSelectedRows
     }) => {

        const {userId} = useAppSelector(({users}) => ({
            userId: users.currentUser.info.id
        }));

        const defaultColumn = React.useMemo(
            () => ({
                Cell: CustomTableCell,
                minWidth: 290,
                width: 290,
                maxWidth: 289,
            }),
            []
        );

        const [hoveredRow, setHoveredRow] = useState<string | null>(null);

        const {
            getTableProps,
            getTableBodyProps,
            headerGroups,
            prepareRow,
            page,
            setPageSize,
            selectedFlatRows,
            state: {pageSize, selectedRowIds},
        } = useTable<IRowData>(
            {
                columns,
                data,
                defaultColumn,
                autoResetFilters: false,
                autoResetSortBy: false,
            },
            useSticky,
            usePagination,
            useRowSelect,
            (hooks) => {
                hooks.visibleColumns.push((columns) => [
                    {
                        id: 'selection',
                        Header: ({getToggleAllRowsSelectedProps}) => (
                            <div className="flex justify-center content-center">
                                <IndeterminateCheckbox
                                    name=""
                                    {...getToggleAllRowsSelectedProps()}
                                />
                            </div>
                        ),
                        Cell: ({row}: any) => (
                            <div className="flex justify-center content-center">
                                <IndeterminateCheckbox
                                    name=""
                                    {...row.getToggleRowSelectedProps()}
                                />
                            </div>
                        ),
                        minWidth: 34,
                        maxWidth: 34,
                        width: 34,
                    },
                    ...columns,
                ]);
            }
        );
        useEffect(() => {
            setSelectedRows(selectedFlatRows)
        }, [selectedFlatRows])

        React.useEffect(() => {
            if (tableSettingsLoaded) {
                setPageSize(tableSettings.limit);
            }
        }, [tableSettingsLoaded]);

        const dataSourceStatusIcon: { [key: string]: string } = {
            current: iconStateSuccess,
            archived: iconStateDisabled,
            // ERROR: iconStateError,
        };

        // console.log(headerGroups)
        // console.log('PAge', page)

        return (
            <div className="table-main overflow-hidden w-full flex flex-col h-full">
                <div className="flex-1 min-h-0 overflow-auto rounded-sm dark:bg-[#0f172a]">

                    <div
                        className={
                            `${rowDataLoading ? 'overflow-hidden min-h-[23.3rem]' : 'overflow-x-auto'} 
               relative 
               ${productsRequestError ? 'min-h-[2rem]' : ''}`
                        }
                    >

                        {rowDataLoading && (
                            <div className="absolute h-1/2 w-full top-[101.75px] flex justify-center items-center">
                                <ComponentsLoader size={100}/>
                            </div>
                        )}

                        <table
                            className="react-table sticky"
                            cellSpacing="0"
                            cellPadding="0"
                            {...getTableProps()}
                        >
                            <thead className="header">
                            {headerGroups.map((headerGroup, index) => (
                                <HeaderRow
                                    key={index}
                                    headerGroup={headerGroup}
                                />
                            ))}
                            </thead>

                            {!rowDataLoading && data.length > 0 && !productsRequestError && (
                                <tbody {...getTableBodyProps()} className="body">
                                {page.map((row) => {
                                    prepareRow(row);
                                    const {attributes} = row.original;
                                    // @ts-ignore
                                    const whType = row.original.attributes.type
                                    const isExternalWarehouse = [WarehouseType.ExternalWarehouseFBO, WarehouseType.ExternalWarehouseFBS].includes(whType)
                                    const isPublicWarehouse = [WarehouseType.PublicFBS].includes(whType)
                                    const isSupplierWarehouse = [WarehouseType.Supplier].includes(whType)
                                    return (
                                        <tr
                                            {...row.getRowProps()}
                                            key={row.id}
                                            onMouseEnter={() => setHoveredRow(row.id)}
                                            onMouseLeave={() => setHoveredRow(null)}
                                            className={hoveredRow === row.id ? 'bg-blue-50/60 dark:bg-blue-900/20' : ''}
                                        >
                                            {row.cells.map((cell) => {

                                                if (cell.column.id === "type") {
                                                    return (
                                                        <td
                                                            {...cell.getCellProps()}
                                                            className="dark:bg-[#0f172a] dark:text-[#d3ddec]"
                                                        >
                                                            <div className="flex">
                                                                {(isExternalWarehouse || isPublicWarehouse) &&
                                                                    <Icon width={20} height={20}
                                                                          marketSource={cell.row.values.source}
                                                                          customClassName={`mr-2`}/>
                                                                }
                                                                <span>{parseWarehouseType(t(whType))}</span>
                                                            </div>

                                                        </td>
                                                    );
                                                }
                                                if (cell.column.id === 'selection') {
                                                    return (
                                                        <td
                                                            {...cell.getCellProps()}
                                                            className="sticky left-0 dark:bg-[#0f172a]"
                                                        >
                                                            {cell.render('Cell')}
                                                        </td>
                                                    );
                                                }
                                                if (cell.column.id === "name") {
                                                    return (
                                                        <td
                                                            {...cell.getCellProps()}
                                                            className="dark:bg-[#0f172a] dark:text-[#d3ddec]"
                                                        >
                                                            <div className={`flex flex-col`}>
                                                                <p title={[382].includes(userId ?? 0) && (attributes['uuid'] || '')}>{cell.render('Cell')}</p>
                                                                {[1].includes(userId ?? 0) && <div className={`text-[12px] text-gray-300 mt-1`}>{
                                                                    // @ts-ignore
                                                                    attributes['uuid'] || ''
                                                                }</div>}
                                                            </div>
                                                        </td>
                                                    );
                                                }

                                                return (
                                                    <td
                                                        {...cell.getCellProps()}
                                                        id={cell.column.id}
                                                        key={cell.column.id}
                                                        className="dark:bg-[#0f172a] dark:text-[#d3ddec]"
                                                    >
                                                        {
                                                            cell.column.id === "source" ?
                                                                (
                                                                    <div className={styles.connectionStatusCell}>
                                                                        {cell.value ?
                                                                            <>
                                                                                <img
                                                                                    // @ts-ignore
                                                                                    src={dataSourceStatusIcon[attributes['sourceConnectionStatus']]}
                                                                                    className={styles.connectionStatusIcon}
                                                                                    alt={''}
                                                                                />
                                                                                <div className={`flex flex-col`}>
                                                                                    <div>{isSupplierWarehouse && cell.value.indexOf('LK') === 0 ? cell.value : t<string>(`Data source ${cell.value}`, {ns: "messages"})}</div>

                                                                                    {isExternalWarehouse && <div
                                                                                        className={`text-[12px] text-gray-400`}>{
                                                                                        // @ts-ignore
                                                                                        row.original.attributes.externalID}
                                                                                    </div>}
                                                                                </div>

                                                                            </>
                                                                            :
                                                                            '-'
                                                                        }
                                                                    </div>
                                                                )
                                                                :
                                                                (
                                                                    cell.column.id === "sourceUpdateFrequency" ?
                                                                        (<>{cell.value ? t(`Update every ${cell.value} minutes`, {ns: "messages"}) : '-'}</>)
                                                                        :
                                                                        (
                                                                            ["isForSale", "isEnableExport"].includes(cell.column.id) ?
                                                                                (<>{cell.value ? t(`Yes`, {ns: "messages"}) : '-'}</>)
                                                                                :
                                                                                cell.render('Cell')
                                                                        )
                                                                )

                                                        }

                                                    </td>
                                                );
                                            })}
                                            <td
                                                className="m-0 p-[8px] min-w-[44px] bg-white dark:bg-[#0f172a] z-10"
                                                data-sticky-first-right-td="true"
                                                {...row.getRowProps()}
                                            >
                                                <div className="flex items-center justify-center relative w-full">
                                                    <button
                                                        onClick={() => editRow(row.original)}
                                                        className={`
                                                            p-1 rounded transition-all cursor-pointer
                                                            ${hoveredRow === row.id
                                                                ? 'opacity-100 hover:bg-blue-50'
                                                                : 'opacity-20'
                                                            }
                                                        `}
                                                        title="Редактировать"
                                                    >
                                                        <img className="w-[14px] h-[14px]" src={pencilIcon} alt="" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            )}
                        </table>

                        {!rowDataLoading && data.length <= 0 && !productsRequestError && (
                            <div className="bg-white dark:bg-[#0f172a] border-b border-l border-r border-[#eef2ff] dark:border-slate-700 p-2 text-center dark:text-[#d3ddec]">У вас
                                еще нет складов</div>
                        )}
                    </div>


                    {productsRequestError && (
                        <div className="font-semibold text-center my-4 text-red-500">
                            При получении продуктов произошла ошибка:
                            {productsRequestError}
                        </div>
                    )}

                </div>

                <TableFooter
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    setTableSettings={setTableSettings}
                    tableSettings={tableSettings}
                    tableSettingsLoaded={tableSettingsLoaded}
                    totalProducts={totalProducts}
                ></TableFooter>

            </div>
        );
    }
);

export default Table;
